/**
 * CollectionHeader - Header section for a record collection
 *
 * Atomic level: Organism
 * Composes: InsightCard
 *
 * Shows stat cards with metrics (total records, average conduct score,
 * dissatisfaction ratio, vulnerability exposure, eligible complaints).
 *
 * Uses aggregateStats from API for accurate statistics across the entire filtered recordset
 * (not just the current page).
 *
 * Following GO UI Design System patterns
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import { CardTitle } from '../../../ui/card';
import { MetricStat } from './MetricStat';
import { getConductScoreColor } from './ConductScoreIndicator';
import { InsightCard } from './InsightCard';
import type { RecordCollection, Record } from '../../../../types/record';
import type { AggregateStats } from '../../../../services/types';

// Accent colors for percentage cards
const COLORS = {
  dissatisfaction: 'var(--go-bittersweet)',
  vulnerability: 'var(--go-purple)',
  eligibleComplaints: 'var(--go-berry)',
};

interface CollectionHeaderProps {
  collection: RecordCollection;
  /** Aggregate statistics from API (required for accurate stats) */
  aggregateStats?: AggregateStats;
  /** Records array - only used for export functionality */
  records?: Record[];
  complaintDetectionEnabled?: boolean;
  vulnerabilityDetectionEnabled?: boolean;
  onComplaintDetectionChange?: (enabled: boolean) => void;
  onVulnerabilityDetectionChange?: (enabled: boolean) => void;
  className?: string;
}

// Default empty stats for when aggregateStats is not provided
const DEFAULT_STATS: AggregateStats = {
  totalRecords: 0,
  avgConductScore: 0,
  dissatisfactionRatio: 0,
  vulnerabilityExposure: 0,
  eligibleComplaintsRatio: 0,
  vulnerableCount: 0,
  eligibleComplaintsCount: 0,
  dissatisfactionByCategory: [],
  vulnerabilityByCategory: [],
  eligibleComplaintsByConfidence: [],
  rootCausesByCategory: [],
  topRootCauseLabels: [],
};

const CollectionHeader: React.FC<CollectionHeaderProps> = ({
  aggregateStats,
  complaintDetectionEnabled = true,
  vulnerabilityDetectionEnabled = true,
  className,
}) => {
  // Dev warning when aggregateStats not provided
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !aggregateStats) {
      console.warn(
        '[CollectionHeader] aggregateStats prop not provided. ' +
        'Stats will show default values. Ensure the API response includes aggregateStats.'
      );
    }
  }, [aggregateStats]);

  // Use aggregateStats from API, fall back to empty stats if not provided
  const stats = aggregateStats ?? DEFAULT_STATS;
  const scoreColor = getConductScoreColor(stats.avgConductScore);

  return (
    <section className={cn('overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[var(--shadow-card)]', className)}>
      <div className="px-5 py-5">
        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            <CardTitle>Conduct overview</CardTitle>
          </div>

          <div className="grid gap-3 lg:max-w-[640px] lg:grid-cols-[minmax(0,1fr)_180px]">
            <div className="rounded-xl border border-border/60 bg-card px-5 py-4 shadow-[var(--shadow-card)]">
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Average conduct score
                </p>
                <div className="flex items-end gap-3">
                  <span
                    className="font-jakarta text-5xl font-extrabold leading-none tracking-[-0.05em]"
                    style={{ color: scoreColor }}
                  >
                    {stats.avgConductScore.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-border/60 bg-muted/25 px-4 py-4">
              <MetricStat value={stats.totalRecords.toLocaleString()} label="Total records" size="sm" />
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 border-t border-border/70 pt-5 md:grid-cols-2 xl:grid-cols-3">
        {/* Dissatisfaction card */}
          <InsightCard
            title="Dissatisfaction Ratio"
            percentage={stats.dissatisfactionRatio}
            color={COLORS.dissatisfaction}
            breakdowns={stats.dissatisfactionByCategory}
          />

          {/* Vulnerability Exposure card */}
          {vulnerabilityDetectionEnabled && (
            <InsightCard
              title="Vulnerability Exposure"
              percentage={stats.vulnerabilityExposure}
              color={COLORS.vulnerability}
              breakdowns={stats.vulnerabilityByCategory}
            />
          )}

          {/* Eligible Complaints card */}
          {complaintDetectionEnabled && (
            <InsightCard
              title="Eligible Complaints"
              percentage={stats.eligibleComplaintsRatio}
              color={COLORS.eligibleComplaints}
              breakdowns={stats.eligibleComplaintsByConfidence}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export { CollectionHeader };
